/**
 * SampleRequestDialog — copy-pasteable curl for calling a model through the
 * inference gateway, with two interactive in-snippet tokens:
 *  - the bearer key: click to create-and-fill (via onCreateKey) or to swap
 *    between the real key and the $TRAINLOOP_API_KEY placeholder
 *  - the "stream" value: click to flip true/false
 *
 * Key state lives in the parent (ApiAccessButtons) because the create-key
 * dialog is shared with the header menu flow.
 */

import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTeam } from '@/contexts/TeamContext'
import type { ApiKeyCreateResponse } from '@/hooks/useApiKeys'
import type { Model } from '@/hooks/useModels'

interface SampleRequestDialogProps {
  model: Model
  open: boolean
  onOpenChange: (open: boolean) => void
  createdKey: ApiKeyCreateResponse | null
  useKey: boolean
  onUseKeyChange: (useKey: boolean) => void
  stream: boolean
  onStreamChange: (stream: boolean) => void
  /** Launch the create-key flow (parent swaps this dialog for the form). */
  onCreateKey: () => void
}

/** Snippet split around the two interactive tokens (bearer key, stream flag)
 * so they render as clickable elements while the copy button still gets the
 * full assembled string. */
function buildCurlParts(model: Model): { before: string; mid: string; end: string } {
  return {
    before: `curl ${model.gateway_url}/v1/chat/completions \\\n  -H "Authorization: Bearer `,
    mid: [
      '" \\',
      '  -H "Content-Type: application/json" \\',
      "  -d '{",
      `    "model": "${model.gateway_model_id}",`,
      '    "messages": [{"role": "user", "content": "Hello!"}],',
      '    "stream": ',
    ].join('\n'),
    end: "\n  }'",
  }
}

function SnippetToken({
  label,
  tooltip,
  onClick,
}: {
  label: string
  tooltip: string
  onClick: () => void
}): React.JSX.Element {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            className="underline decoration-muted-foreground decoration-dotted underline-offset-2 transition-colors hover:decoration-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {label}
          </button>
        </TooltipTrigger>
        <TooltipContent className="px-2 py-1 text-xs">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function ApiKeyBox({ apiKey }: { apiKey: ApiKeyCreateResponse }): React.JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label>API key &ldquo;{apiKey.name}&rdquo;</Label>
      <div className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2">
        <code className="flex-1 break-all font-mono text-xs">{apiKey.api_key}</code>
        <CopyButton value={apiKey.api_key} label="Copy API key" />
      </div>
      <p className="text-xs text-muted-foreground">
        Shown only while you stay on this page. Copy it before leaving.
      </p>
    </div>
  )
}

export function SampleRequestDialog({
  model,
  open,
  onOpenChange,
  createdKey,
  useKey,
  onUseKeyChange,
  stream,
  onStreamChange,
  onCreateKey,
}: SampleRequestDialogProps): React.JSX.Element {
  const { isRestricted } = useTeam()
  const { before, mid, end } = buildCurlParts(model)
  const token = useKey && createdKey ? createdKey.api_key : '$TRAINLOOP_API_KEY'
  const snippet = before + token + mid + String(stream) + end

  // Restricted demo teams (TRA-832) can't mint keys: the token renders as a
  // plain placeholder, never a create-and-fill affordance.
  const isKeyClickable = createdKey !== null || !isRestricted
  const keyTooltip =
    createdKey === null
      ? 'Create an API key and fill it in'
      : useKey
        ? 'Switch back to the placeholder'
        : `Fill in key "${createdKey.name}"`

  function handleKeyTokenClick(): void {
    if (createdKey === null) {
      onCreateKey()
    } else {
      onUseKeyChange(!useKey)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* 4xl: wide enough that the Authorization line fits without a
          horizontal scroll even with a real llmgw_ key filled in. */}
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Sample request</DialogTitle>
          <DialogDescription>
            Call <span className="font-mono">{model.gateway_model_id}</span> through
            the inference gateway from any OpenAI-compatible client.
          </DialogDescription>
        </DialogHeader>

        {/* min-w-0: DialogContent is a grid — without it the long curl line
            sets the column's intrinsic width and blows out the dialog. */}
        <div className="relative min-w-0">
          <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 p-4 pr-10 text-xs text-foreground">
            {before}
            {isKeyClickable ? (
              <SnippetToken label={token} tooltip={keyTooltip} onClick={handleKeyTokenClick} />
            ) : (
              token
            )}
            {mid}
            <SnippetToken
              label={String(stream)}
              tooltip={stream ? 'Turn streaming off' : 'Turn streaming on'}
              onClick={() => { onStreamChange(!stream) }}
            />
            {end}
          </pre>
          <CopyButton
            value={snippet}
            label="Copy sample request"
            className="absolute right-2 top-2"
          />
        </div>

        {createdKey === null ? (
          <p className="text-xs text-muted-foreground">
            {isRestricted ? (
              <>
                Set <span className="font-mono">$TRAINLOOP_API_KEY</span> to your
                team&apos;s API key.
              </>
            ) : (
              <>
                Click <span className="font-mono">$TRAINLOOP_API_KEY</span> in the
                snippet to create a key and fill it in.
              </>
            )}{' '}
            Click the <span className="font-mono">"stream"</span> value to toggle
            streaming.
          </p>
        ) : (
          <ApiKeyBox apiKey={createdKey} />
        )}

        <DialogFooter>
          <Button size="sm" onClick={() => { onOpenChange(false) }}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
